import { formatDollars } from '@fruitful/fruitils';
import {
    buildInvestmentWaterfall,
    type FeeComparison,
} from '@fruitful/money-map-simulator';
import React, { useMemo } from 'react';
import {
    BodyMediumRegular,
    FontFamilyName,
    LabelRegular,
    Shadows,
    Text,
    XStack,
    YStack,
} from 'smoothie';

import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import {
    Header,
    InvestingDisclosures,
    LinearGradientBackgroundCard,
    Section,
} from './shared';
import { SlideNextButton } from './SlideNextButton';

const FeeComparisonCard = ({
    feeComparison,
}: {
    feeComparison: FeeComparison;
}) => (
    <YStack
        width="100%"
        padding={16}
        backgroundColor="rgba(255, 255, 255, 0.5)"
        borderRadius={16}
        borderWidth={1}
        borderColor="$white"
        gap={16}
        {...Shadows.MEDIUM}
    >
        <YStack gap={8}>
            <LabelRegular
                textTransform="uppercase"
                letterSpacing={0.5}
                fontSize={10}
                fontWeight={600}
            >
                total balance after {feeComparison.years} years
            </LabelRegular>
            <Text
                fontSize={48}
                lineHeight={48}
                letterSpacing="-2.4px"
                style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
            >
                {/* rows[0] is always Fruitful (0% fee) */}
                {formatDollars(
                    (feeComparison.rows[0]?.endBalanceCents ?? 0) / 100,
                    false,
                )}
            </Text>
        </YStack>

        <YStack gap={0} width="100%">
            <BodyMediumRegular
                fontWeight="600"
                paddingBottom={8}
                borderBottomWidth={1}
                borderColor="rgba(0,0,0,0.06)"
            >
                Management fees paid over {feeComparison.years} years
            </BodyMediumRegular>
            {feeComparison.rows.map((row) => (
                <XStack
                    key={row.provider}
                    width="100%"
                    paddingVertical={4}
                    borderBottomWidth={1}
                    borderColor="rgba(0,0,0,0.06)"
                >
                    <BodyMediumRegular flex={1}>
                        {row.provider}
                    </BodyMediumRegular>
                    <BodyMediumRegular width={56} textAlign="right">
                        {row.feePercent.toFixed(2)}%
                    </BodyMediumRegular>
                    <BodyMediumRegular width={74} textAlign="right">
                        {formatDollars(row.totalFeesCents / 100, false)}
                    </BodyMediumRegular>
                </XStack>
            ))}
        </YStack>

        <LabelRegular color="$gray1000">
            All 3 fee calculations assume an annualized rate of return of{' '}
            {feeComparison.assumedReturnPercent}%, a starting balance of{' '}
            {formatDollars(feeComparison.startingBalanceCents / 100, false)},
            and annual contributions of{' '}
            {formatDollars(feeComparison.annualContributionCents / 100, false)}.
            An investor may have outcomes that are different than what is
            displayed. All investments have risk.
        </LabelRegular>
    </YStack>
);

const WhyInvestSlide = () => {
    const { results } = useConversationalMMContext();
    const investment = results.rawChatResults.investment ?? null;

    const feeComparison = useMemo(() => {
        if (investment == null) return null;
        return buildInvestmentWaterfall(investment).feeComparison;
    }, [investment]);

    return (
        <YStack gap={24}>
            <LinearGradientBackgroundCard>
                <YStack
                    gap={24}
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Header title="Why invest at Fruitful" />

                    <Section
                        title="0% Management fees"
                        body="We don’t charge extra for investment management—it's included in your membership. How we compare:"
                    />

                    {feeComparison != null && (
                        <FeeComparisonCard feeComparison={feeComparison} />
                    )}

                    <SlideNextButton />
                </YStack>
            </LinearGradientBackgroundCard>

            <InvestingDisclosures />
        </YStack>
    );
};

export default WhyInvestSlide;
